import { Entry, FiltrableArticleAttributes, SeoMetadata } from "@utils/damac/api";
import { ChangeEventHandler, MouseEventHandler, ReactElement, ReactPortal } from "react";
import { ControllerFieldState, ControllerRenderProps } from "react-hook-form";

import { EventComponentItem } from "@/components/EventComponent/type";

// import { SelectOptions } from "@/components/ui/Form/Select/SelectBase";

export enum LinkType {
  EXTERNAL = "external",
  CONTENT = "content",
}
export interface Media {
  title: string;
  description?: string;
  url: string;
  width?: number;
  height?: number;
  size?: number;
}

export enum VisualType {
  LABEL = "label",
  IMAGE = "image",
  ICON = "icon",
}
export enum Target {
  BLANK = "_blank",
  SELF = "_self",
}

export enum TargetSite {
  corporate = "Corporate",
  properties = "Properties",
  "damac-digital" = "damac-digital",
}

export enum Site {
  corporate = "corporate",
  properties = "properties",
  damacDigital = "damac-digital",
}

export interface BaseEntry {
  id: string;
  type: string;
  errorMessage?: string;
}
export interface CustomLink extends BaseEntry {
  label: string;
  link: {
    linkType: LinkType | string;
    visualType: VisualType | string;
    url: string;
    target: Target | string;
    icon?: string;
    category?: string;
    content?: {
      slug: string;
      type: string;
      params?: Record<string, any>;
    };
    image?: {
      url: string;
      name: string;
      description: string;
    };
  };
}

export interface BreadCrumb extends CustomLink {
  disabled?: boolean;
}
export interface UnitAnchorsProps {
  anchors: UnitAnchor[];
}

export interface UnitAnchor {
  label: string;
  component: "unit-list" | "city-location" | "payment-plan" | "mortgage" | "project";
}

export interface NavigationStructure {
  highlightFirstCluster: boolean;
  items: NavigationItem[];
  name: string;
}
export interface NavigationItem {
  id: string;
  title: string;
  columns?: NavigationColumm[];
  menuItemCta?: CustomLink;
  urlSlug?: string;
}
export interface NavigationColumm {
  id: string;
  title: string;
  links: CustomLink[];
  viewAll?: CustomLink;
}

export type BreadcrumbEntry = {
  id: string;
  breadcrumbItems: CustomLink[];
  color: string;
};

export type BreadcrumbProps = {
  entry: BreadcrumbEntry;
  disabledBehaviour?: boolean;
  wrapperClasses?: string;
  labelClasses?: string;
  substringNumber?: number;
};

export interface HeaderProps {
  locale: string;
  logoDark: Media;
  logoLight: Media;
  search?: CustomLink;
  shortlist?: CustomLink;
  personalArea?: CustomLink;
  menu: NavigationStructure;
  contactChannels: CustomLink[];
  getInTouch: CustomLink;
  settings: Record<string, any>;
  minimal?: boolean;
  corporate?: boolean;
  onLogin?: (success: boolean, otpError?: boolean) => void;
  target?: Site;
}
export interface HamburgerMenuProps {
  onClick?: Function;
  variant: string;
}

type ReactText = string | number;
type ReactChild = ReactElement | ReactText;

interface ReactNodeArray extends Array<ReactNode> {}
type ReactFragment = {} | ReactNodeArray;
export type ReactNode = ReactChild | ReactFragment | ReactPortal | boolean | null | undefined;

export interface HeaderSettingsProps {
  settings: {
    currencies: string[];
    measurements: string[];
    locales: string[];
    languages: string[];
    preheader?: boolean;
  };
}

/*
  AutogeneratedCarousel;
*/
export enum CardType {
  PROJECT = "project",
  "UNIT-TYPE" = "unit-type",
  EDITORIAL = "editorialCarousel",
  ARTICLE = "articleCarousel",
  VIDEO = "videoCarousel",
  "FEW-RESULTS" = "few-results",
}

type Tag = {
  label: string;
  isHiglighted: boolean;
};

export interface CardTagsProps {
  tags: Tag[];
  className?: string;
}
export interface GroupItem {
  logo?: Media;
  images?: Media[];
  id: string;
  tags?: Tag[];
  title: string;
  location?: string;
  position?: Position;
  price?: number;
  bedrooms?: LabelValue;
  unitType?: string;
  cta: CustomLink;
  sqFt?: {
    label: string;
    value: string | number;
  };
  availabilityDate?: string;
  project?: string;
  projectID?: number | string;
  assignedCategory?: number | string;
  locationID?: number | string;
  slug?: string;
  projectSlug?: string;
  isPropertyType?: boolean;
}

export interface VideoModalDialogProps {
  title: string | undefined;
  video: VideoElement | undefined;
  closeModal?: () => void;
}

export interface Groups {
  label: string;
  items: (GroupItem | UnitTypeGroupItem | EditorialGroupItem | VideoGroupItem)[];
  cta?: CustomLink;
}

export enum ArticleTypes {
  blog = "blog",
  blogs = "blog",
  news = "news",
  indeustryNews = "industry-news",
  pressRelease = "press-releases",
}

export type ArticleCarouselProps = {
  entry: ArticleCarousel;
  fallbackImage: ImageElement;
};

export type ArticleCarousel = {
  id: string;
  internalName: string;
  centeredTitle: boolean;
  cards: Article[];
  articleType: ArticleTypes;
  count: number;
  cta: CustomLink;
};

export type ColoredText = {
  text: string;
  color: string;
};

export type Article = {
  slug: string;
  type: string;
  internalName: string;
  postDate?: string;
  tags?: string[];
  image?: ImageElement;
  video?: VideoElement;
  body?: string;
  cta?: CustomLink;
  components?: BaseEntry[];
  secondaryTags?: ColoredText[];
  summary: string;
  centerContent?: boolean;
  articleType: ArticleTypes;
  abstract?: string;
  featured?: boolean;
  backgroundTags?: string[];
  seoMetadata?: SeoMetadata;
  corporateSeoMetadata?: SeoMetadata;
  target?: TargetSite[];
  localePublished?: boolean;
  updatedAt?: string;
};

export type ArticleProps = {
  entry: Article;
};

export type AutogeneratedCarouselCard = {
  backupVideoImage?: ImageElement;
  cta: CustomLink;
  image?: ImageElement;
  isCurrentSlide?: string | number;
  subtitle?: string;
  title: string;
  type?: string;
  video?: VideoElement;
  date?: string;
  sectionArea?: string;
  tags?: string[];
  bedrooms?: LabelValue;
  inventoryAvailable?: boolean;
  images?: Media[];
  status?: string;
  furniture?: string;
  price?: number;
  text?: string;
  id: string;
  unitType?: string;
  categoryID?: string;
  slug?: string;
  projectSlug?: string;
  locationID?: string | number;
};

export type AutogeneratedCarouselEntry = BaseEntry & {
  title?: string;
  showAsCardList?: boolean;
  backgroundColor?: string;
  variant?: boolean; // VARIANT FOR HUB
  groups: AutogeneratedCarouselCardGroup[];
  cta?: CustomLink;
  type: CardType;
  titleCenter?: boolean;
};

export type AutogeneratedCarouselProps = {
  entry: AutogeneratedCarouselEntry;
  serverSideCookieCurrency?: string;
  currencyFromApiRes?: string;
};

export type AutogeneratedCarouselCardGroup = {
  id: string;
  cards: AutogeneratedCarouselCard[];
  cta?: CustomLink;
  label?: string;
  highlightProjectTag?: boolean;
  project?: string;
};

export type AutogeneratedCardsProps = AutogeneratedCarouselCardGroup & {
  cardType: CardType;
  variant?: boolean;
  showAsCardList?: boolean;
  serverSideCookieCurrency?: string;
  currencyFromApiRes?: string;
};

export interface UnitTypeGroupItem extends GroupItem {
  status?: string;
  furniture?: string;
  images: Media[];
  price: number;
  bedrooms: LabelValue;
}

export interface FavoriteItem {
  inventoryID: string;
  id: string;
  name: string;
  images: string[];
  country: string;
  countryID: string;
  city: string;
  cityID: string;
  area: string;
  areaID: string;
  project: string;
  projectID: string;
  bedrooms: number[];
  areaMax: string;
  priceMin: number;
  priceMax: number;
  status: string;
  launchDate: string;
  labels: string;
  areaMin: string;
  brand: string;
  handoverDate: string;
  isFurnished: boolean;
  deliveryDate: string;
  type: string;
  price: number;
  propertyName: any;
}

export interface UnitTypeCardListProps {
  item: UnitTypeGroupItem;
  customClass?: string;
  serverSideCookieCurrency?: string;
  currencyFromApiRes?: string;
  isSearchResultPage?: boolean;
}

export interface EditorialGroupItem extends GroupItem {
  subtitle?: string;
  text?: string;
}

export interface EditorialCardListProps {
  item: AutogeneratedCarouselCard;
  customClass?: string;
}

export interface ArticleGroupItem extends GroupItem {
  video?: {
    url: string;
    poster: string;
    title: string;
  };
  data?: string;
  sectionArea?: string;
}

export interface ArticleCardListProps {
  item: AutogeneratedCarouselCard;
}

export interface VideoGroupItem extends GroupItem {
  video?: {
    url: string;
    poster: string;
    title: string;
  };
  customClass?: string;
}

export interface VideoCardListProps {
  entry: AutogeneratedCarouselCard;
  isCurrentSlide?: boolean;
  openVideoModal: (v: VideoModalDialogProps) => void;
}

/*
  End AutogeneratedCarousel;
*/

export interface GalleryModalProps {
  medias: Media[];
  modalOpen: boolean;
  closeModal: () => void;
}

/*
  Guided Tool;
*/

export type GuidedToolSlideOptions = {
  id: string;
  title?: string;
  description?: string;
  image?: Media;
};

export type GuidedToolSlide = {
  id: string;
  suggestion: string;
  question: string;
  type: "editorial" | "filter";
  variant: "single" | "multi" | "range";
  options: GuidedToolSlideOptions[];
  filtersWithImage?: { [key: string]: Media };
  rangeMin?: number;
  rangeMax?: number;
  icon?: Media;
  allowRangeSelection?: boolean;
  filterType?: string;
  locations?: string[];
};

export type GuidedToolSlideProps = {
  slide: GuidedToolSlide;
  isPriceRange?: boolean;
};

export type GuidedTool = {
  title: string;
  background: Media;
  slides: GuidedToolSlide[];
};

export type GuidedToolProps = {
  guidedTool: GuidedTool;
  modalOpen: boolean;
  closeModal: () => void;
};

export type GuidedToolCardItemProps = {
  card: GuidedToolCardItem;
};

export type GuidedToolCardItem = {
  id: string;
  image: Media;
  title: string;
  linkType: LinkType;
  url?: string;
  link?: CustomLink;
  modal?: GuidedTool;
};

export type ModalToolOption = {
  isChecked?: boolean;
  onCheckChange: (id: string) => void;
  option: GuidedToolSlideOptions;
  variant?: "single" | "multi" | "range";
  optionsLenght?: number;
  forwardId?: boolean;
};

export type BeedromFiltersProps = {
  options: FacetValue[];
  slideId: string;
};

export type GuidedToolLauncherEntry = {
  title: string;
  backgroundColor?: string;
  secondaryTitle?: string;
  cta?: { label: string; link: string };
  cards: GuidedToolCardItem[];
  loggedInCards: GuidedToolCardItem[];
  guestCards: GuidedToolCardItem[];
};
export type GuidedToolLauncherProps = {
  entry: GuidedToolLauncherEntry;
};

//TODO this type should be something more similar to CommercetoolsProject
export type ProjectCardListProps = {
  item: GroupItem & {
    images: Media[];
    bedrooms: LabelValue;
    title: string;
  };
  displayMapPin?: boolean;
  onPinSelected?: () => void;
  customClass?: string;
  finalUrl?: any;
  projectResultPage?: boolean;
};

/* OPT INPUT */
export interface OtpInputProps {
  value: string;
  valueLength: number;
  onChange: (value: string) => void;
}

/* SEARCH RESULT */
export type Result = {
  cardType: CardType;
  items: (GroupItem | UnitTypeGroupItem)[];
};

type Labels = {
  [key: string]: string;
};

export interface SearchResultStickyBarProps {
  cardType?: CardType;
  sortingFilters?: FilterCategory;
  switchHandler?: (value: CardType) => void;
  checked?: boolean;
  labels?: Labels;
  toggleChecked?: () => void;
}

type FilterValue = {
  label: string;
  value: string;
};

export type FilterCategory = {
  label: string;
  values: FilterValue[];
};

export interface SearchResultFilterBarProps {
  filterCategoryList: FilterCategory[];
}

export interface SwitchButtonProps {
  label: string;
  selected: boolean;
  type?: "switch" | "button";
  customClass?: string;
  clickHandler: MouseEventHandler;
}

export interface Position {
  lat: number;
  lng: number;
  place_id: string;
  icon: string;
  title?: string;
  coordinates?: {
    lat: number;
    lon: number;
  };
}

/* TOGGLE */
export interface ToggleProps {
  label: string;
  checked: boolean;
  disabled: boolean;
  changeHandler: ChangeEventHandler<HTMLInputElement>;
  labelOnRight?: boolean;
}

/* LOGIN DIALOG */
export interface LoginDialogProps {
  btnLabel: string;
  buttonSmall?: boolean;

  customFunc: Function;
}

/*
  Available Units List
*/

export enum AULFiltersType {
  PLAN_TYPE = "planType",
  FLOOR = "floor",
  VIEW = "unitViews",
}

export type FloorPlan = {
  floor: string;
  floorBullet?: string;
  image: Media;
};

interface CommerceToolsUnitsOrProject {
  id: string;
  country: string;
  city: string;
  area: string;
  location: string;
  countryID: string;
  cityID: string;
  areaID: string;
  locationID: string;
  project: string;
  projectID: string;
  latitude: number;
  longitude: number;
  isFurnished: boolean;
  deliveryDate: string | Date;
  launchDate: string | Date;
  name: string;
  inventoryID: string;
  gallery?: string[];
  images?: string[];
  slug: string;
  sellable: boolean;
  inventoryAvailable?: boolean;
  features: string[];
  serverSorting?: number;
}

export interface PropertyName {
  en?: string;
}

export interface CommercetoolsProject extends CommerceToolsUnitsOrProject {
  propertyType: string[];
  areaMin: number;
  areaMax: number;
  status?: string;
  identifier?: string;
  brands?: string;
  priceMin: number;
  priceMax: number;
  price?: number;
  tokenAmount?: number;
  bedrooms?: string[] | string;
  others: string[];
  offers: string[];
  features: string[];
  lifeStyle: string[];
  type: "project" | "propertyType" | "unit-type";
  launch?: boolean;
  availableUnits?: number;
  soldUnits?: number;
  projectName: string;
  currency: string;
  unitType?: string;
  areaSqft?: number;
  parkings?: number;
  eoiAmount?: {
    currencyCode: string;
    centAmount: number;
  };
  assignedCategory: string;
  bathrooms: number[];
  views: string[];
  priceMaxCurrencies?: Record<string, number>;
  priceMinCurrencies?: Record<string, number>;
  areaMaxConversion?: Record<string, number>;
  areaMinConversion?: Record<string, number>;
  category?: string[];
  propertyName?: PropertyName;
  sfLastModifiedDate?: string;
}

export interface CommercetoolsCategoryCustom {
  customFieldsRaw: {
    name: string;
    value: string;
  }[];
}
export interface CommercetoolsCategory {
  id: string;
  name: string;
  custom: CommercetoolsCategoryCustom;
  ancestors?: CommercetoolsCategory[];
}

export interface CommercetoolsUnit extends CommerceToolsUnitsOrProject {
  bedrooms: number;
  floor: number;
  unitPlan: string;
  floorPlan: string;
  virtualTourUrl: string;
  identifier: string;
  areaSqft: number;
  areaSqmt: number;
  balcony: number;
  bathrooms: number;
  parkings: number;
  unitType: string;
  price: number;
  type: "unitType";
  propertyName?: string;
  currency: string;
  status: string;
  tags?: Tag[] | undefined;
}

export type PlanTypeFilter = BaseEntry & {
  label: string;
  description: {
    measure: string;
    bathroom: string;
  };
  disabled?: boolean;
  link: {
    icon: string;
  };
};
export type FloorFilters = [number, number] | undefined;

export type ViewsFilter = {
  id: string;
  value: string;
  label: string;
  disabled?: boolean;
  link: {
    icon: string;
  };
};

export type AULFilterProps = {
  title: string;
  // filters?: PlanTypeFilter[] | FloorFilters | ViewsFilter[];
  // filters: ProductsFilter;
  filters: ProductsFiltersTerms[];
  filterType: string;
  selectedFloorFilters?: FloorFilters;
  icon?: string;
  setLastFilterTouched: (v: AULFiltersType) => void;
  setSelectedPlanFilters?: (v: string[]) => void;
  setSelectedFloorFilters?: (v: FloorFilters) => void;
  setSelectedViewsFilters?: (v: string[]) => void;
};

export type AULFilter = {
  [key: string]: CustomLink;
};

export type floorPlanModal = {
  unit: CommercetoolsUnit;
};

export type UnitAccordionProps = {
  unit: CommercetoolsUnit;
  index: number;
  openAccordionIndex: number;
  setOpenAccordionIndex: (v: number) => void;
};

export type AvailableUnitsFilterBarProps = {
  name: string;
  description: string;
  filters: ProductsFilters;
  planTypes: PlanTypeFilter[];
  views: ViewsFilter[];
  floor: [number, number];
  units: CommercetoolsUnit[];
  filteredUnits: CommercetoolsUnit[];
  setFilteredUnits: (v: CommercetoolsUnit[]) => void;
};

export interface Facet {
  [name: string]: FacetValue[];
}
export interface Facets {
  [name: string]: FacetValue[];
}
export interface FacetValue {
  count: number;
  value: string | number;
  enabled?: boolean;
  checked?: boolean;
}

export type AvailableUnitsListProps = {
  title?: string;
  name?: string;
  description?: string;
  results?: CommercetoolsUnit[];
  filters?: Facet[];
  total?: number;
  count?: number;
  category: string;
  bedrooms?: number;
  locale: string;
  propertyType: string;
  unitName?: string;
  project?: string;
  propertyTypeUrl?: string;
  projectUrl?: string;
  hasOtherSolutions?: boolean;
  serverSideCookieCurrency?: string;
  currencyFromApiRes?: string;
  isBelongToCommunity?: boolean | void;
  matchedCommunity?: any;
};

/* SEARCH PANEL FILTERS */
interface PanelFiltersValueListItem {
  label: string;
  value: string;
  icon?: string;
  logo?: Media;
  isSelected?: boolean;
  disabled?: boolean;
}
export interface PanelFiltersRangeValueList {
  min: number;
  max: number;
}
export interface PanelFilter {
  title?: string;
  valueList: PanelFiltersValueListItem[] | PanelFiltersRangeValueList;
  delivery?: string;
  selectAllLabel?: string;
  deselectAllLabel?: string;
  showMoreLabel?: string;
  showLessLabel?: string;
  minimunLabel?: string;
  maximunLabel?: string;
}
interface PropertyFilter {
  title?: string;
  unitType?: PanelFilter;
  bedrooms?: PanelFilter;
  bathrooms?: PanelFilter;
  price?: PanelFilter;
  area?: PanelFilter;
  status?: PanelFilter;
  furnishType?: PanelFilter;
  propertyView?: PanelFilter;
  propertyFeatures?: PanelFilter;
}
interface ProjectFilter {
  title?: string;
  delivery?: PanelFilter;
  lifeStyle?: PanelFilter;
  projectAmenities?: PanelFilter;
  brandCollaborations?: PanelFilter;
}
interface OtherFilter extends PanelFilter {
  other?: PanelFilter;
  offers?: PanelFilter;
}

export interface RangeSliderFilterProps {
  filter: PanelFilter;
  selectOptions?: SelectOptions[];
  border?: boolean;
  hideTitle?: boolean;
  inputOnTop?: boolean;
  customClass?: string;
  customMeasure?: string;
  returnDataFunc?: (item: number[]) => void;
}

export interface PanelFilters {
  title: string;
  resultsLabel: string;
  clearAllLabel: string;
  showLabel: string;
  doneLabel: string;
  property: PropertyFilter;
  project: ProjectFilter;
  other: OtherFilter;
}
export interface SearchPanelFiltersProps {
  filters: PanelFilters;
}

export interface OptDialogProps {
  userPhoneNumber: string;
  valueLength: number;
  customFunc?: (otp?: string, otpType?: string) => void;
  serverOtpError?: string;
  resendOtp?: boolean;
}
export interface PanelFiltersListProps {
  filter: PanelFilter;
  border?: boolean;
  showSelectAll?: boolean;
  returnDataFunc?: (item: string[]) => void;
  hideTitle?: boolean;
  customClass?: string;
}

export interface ImageType {
  src: string;
  alt: string;
  width?: number;
  height?: number;
}

export interface ButtonType {
  label: string;
  link: string;
  externalLink?: boolean;
  size: string;
  theme: string;
  variant: string;
  icon?: string;
}

export interface ThankYouHeaderData {
  eoi?: boolean;
  imageTop: ImageType;
  imageLogo: ImageType;
  buttonVisit: ButtonType;
  reservationNumber: string;
  emailValue: string;
  phonevalue?: string;
  event?: EventComponentItem; // TODO handle it one day
  firstName: string;
  voucherNumber: string;
  receiptID?: string;
  damacLivingShortLink: string;
}

export enum MediaComponentType {
  FULL_SCREEN = "full-screen",
  HORIZONTAL = "horizontal",
  VERTICAL = "vertical",
}

export type StorytellingCarouselProps = {
  entry: StoryTellingEntry;
};

export type StorytellingCard = {
  image: ImageElement;
  logo: ImageElement;
  title: string;
  cta: CustomLink;
};

export type StoryTellingEntry = {
  variant: string;
  cards: StorytellingCard[];
};

export interface MediaComponentProps {
  entry: MediaComponent;
}

export interface VideoModal extends BaseEntry {
  buttonLabel: string;
  video: Media;
  mobileVideo?: Media;
  embeddedVideo: CustomLink;
}

export interface MediaComponent extends BaseEntry {
  background: Media;
  backgroundColor: string;
  backupVideoImage: ImageElement;
  componentName: string;
  category: string;
  ctaMediaBanner?: CustomLink | VideoModal;
  modalLabel: string;
  episodeTitle: string;
  gradientColor: GradientColor;
  id: string;
  image: ImageElement;
  mobileImage: ImageElement;
  mobileVideo: VideoElement;
  podcastTitle: string;
  type: MediaComponentType | string;
  video: VideoElement;
  variant: string;
  isImageAnimation: boolean;
}

export type GradientColor = {
  id: string;
  name: string;
  value: string;
};

export type MediaBannerHighlightContent = {
  backupVideoImage?: ImageElement;
  description?: string;
  gradientColor?: GradientColor;
  image?: ImageElement;
  logo: ImageElement;
  mobileImage?: ImageElement;
  mobileVideo?: VideoElement;
  modalTitle?: string;
  primaryCta?: CustomLink;
  modal?: textImage;
  secondaryCta?: CustomLink;
  subtitle?: string;
  title: string;
  video?: VideoElement;
};

export enum MediaBannerHighlightType {
  FULL_SCREEN = "full-screen",
  WRAPPED = "wrapped",
  BOXED = "boxed",
  BOXED_CAROUSEL = "boxed-carousel",
  SEPARATOR = "separator",
}

export type MediaBannerHighlightProps = {
  animation?: boolean;
  boxAlignment: "left" | "center" | "right" | string;
  content: MediaBannerHighlightContent[];
  variant: MediaBannerHighlightType | string;
  wrappedText?: boolean;
  parallaxAnimation?: boolean;
  titleColor?: string;
  backgroundColor?: string;
  backgroundOpacity?: number;
};

export type ImageElement = {
  description?: string;
  height?: number;
  title?: string;
  url: string;
  width?: number;
};

export type VideoElement = {
  backgroundImage?: ImageElement;
  poster?: string;
  title: string;
  url: string;
};

export type FeatureListElement = BaseEntry & {
  componentName: string;
  cardLink?: CustomLink;
  image?: ImageElement;
  title?: string;
  overtitle?: string; // corporate integration
  subtitle?: string; // corporate integration
};

export type FeatureListProps = {
  entry: FeatureListEntry;
};

export type FeatureListEntry = BaseEntry & {
  backgroundToggle: boolean;
  baseElements: FeatureListElement[];
  componentName: string;
  cta?: CustomLink;
  shadowToggle: boolean;
  text?: string;
  title?: string;
  titleVariant?: string;
};
export interface SearchResultBannerItem {
  image: {
    url: string;
    name: string;
    title: string;
    description: string;
  };
  logo: {
    url: string;
    name: string;
    description: string;
  };
  cta: CustomLink;
}

export interface SearchResultBannerProps {
  items: SearchResultBannerItem[];
}

type SlotItem = {
  image: {
    url: string;
    name: string;
    title: string;
    description: string;
  };
  title: string;
  text: string;
  cta: CustomLink;
};
export interface SearchResultEditorialSlotProps {
  item: SlotItem;
}
export interface ProductsFilters {
  [key: string]: ProductsFilter;
}
export interface ProductsFilter {
  total: number;
  terms: ProductsFiltersTerms[];
}
export interface ProductsFiltersTerms {
  term: string;
  count: number;
  disabled?: boolean;
}

export interface BentoListProps {
  entry: BentoList;
}
export interface BentoBlockProps {
  key: number;
  block: BentoListModule;
}
export interface BentoList extends BaseEntry {
  title?: string;
  subtitle?: string;
  modules: BentoListModule[];
}
export interface BentoListModule extends BaseEntry {
  elements: BentoElement[];
  layout: "Horizontal" | "Vertical";
}
export interface BentoElement extends BaseEntry {
  type: BentoElementType;
}
export interface BentoTextElement extends BentoElement {
  icon: CustomLink;
  title: string;
  text: string;
}

export enum BentoElementType {
  text = "bentoTextElement",
  image = "editorialModuleImage",
}
export interface EditorialModuleImage extends BentoElement {
  image: Media;
  mobileImage?: ImageElement;
}

export interface BentoTextElementProps {
  slideIndex: number;
  entry: BentoTextElement;
}

export interface BentoBlockPropsProperties {
  size: string;
  items: BentoTiledProperties[];
  version: string;
  bg: string;
}

export interface BentoTiledProperties {
  icon?: string;
  title?: string;
  content?: string;
  image?: ImageType;
}

export interface HighlightDualColumnProps {
  entry: HighlightDualColumn;
}

export interface HighlightDualColumn {
  overtitle?: string;
  title: string;
  columns: [HighlightColumn, HighlightColumn];
}

interface HighlightColumn {
  backgroundImage: Media;
  logo?: Media;
  title?: string;
  cta?: CustomLink;
  ctaButton: boolean;
}

export interface ProjectSettings {
  settings: {
    currencies: string[];
    measurements: string[];
    locales: string[];
    languages: string[];
    trendingSearch: string[];
    fewResultsMaximum: number;
  };
}
export interface UserSettings {
  language: string;
  currency: "AED" | "USD" | "CNY" | "RUB" | "EUR" | "GBP" | "INR";
  measurement: "imperial" | "metric";
  country: string;
  trendingSearch: string[];
  recentSearch: string[];
  fewResultsMaximum: number;
}
export type TypeLogin = {
  type: string;
};

/* PERSONAL AREA */

export interface MyProfileSetting {
  title: string;
  subtitle: string;
  text: string;
  list: {
    icon: string;
    label: string;
  }[];
}

export interface Profile {
  first_name: string;
  last_name: string;
  email: string;
  nationality: string;
  gender: string;
  phone: string;
  country: string;
  passport_number: string;
}

export interface MyProfileProps {
  account?: {
    first_name: string;
    // @TODO: check dopo merge Biz
    last_name: string;
    email: string;
    gender: string;
    phone: string;
    picture: string;
    role: string;
  };
  profile: Profile;
  damacLiving: {
    logo: Media;
    title: string;
    subtitle: string;
    // @TODO: check dopo merge Biz
    cta: {
      label: string;
      url: string;
    };
  };
  settings: MyProfileSetting;
}

export interface MyProfileSettingModalProps {
  closeModal: () => void;
  modalOpen: boolean;
  settings: MyProfileSetting;
}

export interface AppointmentDetailsModalProps {
  closeModal: () => void;
  appointment: SingleAppointmentDataProps | null;
}

export interface ReservationDetailsModalProps {
  closeModal: () => void;
  reservation: null | ReservationProps;
}

export interface AppointmentsHistoryModalProps {
  closeModal: () => void;
  modalOpen: boolean;
  appointmentsHistory: SingleAppointmentDataProps[];
}

export interface ReservationsHistoryModalProps {
  closeModal: () => void;
  modalOpen: boolean;
  reservationsHistory: ReservationProps[];
  setCurrentReservation: (r: ReservationProps) => void;
}

/* My account Saved Units*/
export interface SavedUnit {
  apartment_id: number;
  price: number;
  square_feet: number;
  bedrooms: number;
  bathrooms: number;
  location: string;
  year_built?: number;
  amenities?: string;
  pet_friendly?: boolean;
  open_house_date: string;
  currency: string;
  picture?: string;
  status?: string;
  closer: boolean;
  images: Media[];
  tags: Tag[];
  cta: CustomLink;
}

export interface SavedUnitsProps {
  savedUnits: any;
}

export interface MyPropertiesProps {
  myProperties: {
    title: string;
    text?: string;
    items: SavedUnit[];
    cta?: CustomLink;
  };
}

export enum AppointmentType {
  LAUNCH_EVENT = "launch event",
  HOUSE_TOUR = "house tour",
  CALL = "call",
  METAVERSE = "metaverse",
  ONLINE_VIRTUAL_TOUR = "online virtual tour",
}

export interface AppointmentUnit {
  name: string;
  bedroomType: string;
  status: string;
  apartment_id: number;
  price: number;
  square_feet: number;
  bedrooms: number;
  bathrooms: number;
  location: string;
  year_built: number;
  amenities: string;
  pet_friendly: boolean;
  open_house_date: string;
  currency: string;
  picture: string;
  closer: boolean;
  launch_date?: string;
  unit_id?: string;
  unit_details?: { label: string; value: string }[];
}

export interface ReservationsProps {
  reservations: ReservationProps[];
  reservationHistory: ReservationProps[];
  profile: Profile;
}

export interface ReservationProps {
  type: string;
  receipt: string;
  reservation_type: "reservation" | "pre-booking" | string;
  reservation_id: string;
  reservationNumber: string;
  total_price: number;
  paid: number;
  currency: string;
  contact_name: string;
  contact_phone: string;
  unit: AppointmentUnit;
  payment: {
    card_holder: string;
    payment_status: string;
    reservation_number: string;
    card_number: string;
    card_expiring: string;
    payed: number;
    currency: string;
  };
  paymentAmount: number;
  unitId: string;
  unitImage: string;
  unitName: string;
  unitPrice: number;
  unitStatus: string;
  unitType: string;
  unitLocation: string;
  bedroomType: string;
  propertyType: string;
}

export enum CookiesType {
  LOGIN_STATE = "login-state",
  LOGIN_TYPE = "loginType",
  ACCOUNT_ID = "account-id",
}

export enum LoginType {
  // SOFT_LOGIN = "soft",
  SOFT_LOGIN = "mobile",
  GOOGLE_LOGIN = "google",
  APPLE_LOGIN = "apple",
  DAMAC_LOGIN = "damac",
  // DAMAC_LOGIN = "credential",
}

export enum LoginDialogType {
  USERNAME_PASSWORD = "username-password",
  OTP = "otp",
  OTP_VERIFICATION = "otp-verification",
}

export interface ColorPicked {
  name: string;
  value: string;
}
/*AutoScrollTile*/
export interface AutoScrollTile extends BaseEntry {
  title: string;
  location: string;
  image: Media;
  logo: Media;
  cta?: CustomLink;
}

export interface AutoScrollTilesEntry extends BaseEntry {
  title: string;
  cards1?: AutoScrollTile[];
  cards2?: AutoScrollTile[];
  cta?: CustomLink;
}
export interface AutoScrollTilesProps {
  entry: AutoScrollTilesEntry;
}

export interface AvailableUnitsFilters {
  floor: number[];
  unitVies: string[];
  planType: string[];
}

export interface EditorialTemplateHeroProps {
  entry: EditorialTemplateHero;
}

export interface EditorialTemplateHero extends BaseEntry {
  logo: ImageElement;
  image?: ImageElement;
  video?: VideoElement;
  title?: string;
  tags?: string[];
  abstract: string;
  breadcrumb: BreadcrumbEntry;
}

export interface EditorialInventoryEntry extends BaseEntry {
  title: string;
  text: string;
  textAlignment?: "left" | "right";
  productsQuery: {};
  cta?: CustomLink;
}
export interface EditorialInventoryProps {
  entry: EditorialInventoryEntry;
  locale?: string;
}

export interface EditorialCard {
  images: {
    url: string;
    title: string;
  }[];
  logo?: {
    url: string;
    alt: string;
  };
  title: string;
  subTitle: string;
  url: string;
}

// export interface ArticleCardProps {
//   id: string;
//   video?: Media;
//   image: Media;
//   title: string;
//   date?: string;
//   sectionArea?: string;
//   tags?: string[];
//   url?: string;
//   abstract?: string;
// }

export interface TileCarouselProps {
  entry: TileCarousel;
}

export interface TileCarousel extends BaseEntry {
  cards: TileCarouselCardProps[];
  cta: CustomLink;
}

export interface TileCarouselCardProps extends BaseEntry {
  image?: ImageElement;
  logo?: ImageElement;
  overtitle?: string;
  title: string;
  titleColor: string;
  abstract: string;
  abstractRtf: {
    html: string;
  };
  link?: CustomLink;
  modal?: TileCarouselCardModal;
  video?: Media;
}

export interface TileCarouselCardModal extends BaseEntry {
  gallery: ImageElement[];
  descriptionTitle?: string;
  subtitle?: string;
  description?: {
    html: string;
  };
  cta: CustomLink;
  layout: "full-screen" | "compact";
}

export interface TileCarouselModalProps {
  closeModal: () => void;
  isOpen: boolean;
  title: string;
  descriptionTitle: string;
  description: string;
  gallery: Media[];
  cta: CustomLink;
}

export interface ArticleFilter {
  title: string;
  valueList: {
    value: string;
    label: string;
  }[];
}
export interface ArticleListingProps {
  entry: ArticleListing;
  filters: FiltrableArticleAttributes;
  fallbackImage?: ImageElement;
  site: Site;
}
export interface ArticleListing extends BaseEntry {
  title: string;
  articles?: Article[];
  variant: "articles" | "offers";
  articlesType: ArticleTypes;
  editorialBox?: EditorialBoxProps;
  sorting?: ArticleSorting;
}

export interface EditorialBoxProps {
  title: string;
  image: Media;
  description: string;
  cta: CustomLink;
}
export interface TelephoneProps {
  prefixName: string;
  phoneName: string;
  reverse?: boolean;
  phoneField?: ControllerRenderProps;
  phoneState?: ControllerFieldState;
  prefixField?: ControllerRenderProps;
  prefixState?: ControllerFieldState;
  onNumberChange?: any;
  onPrefixChange?: any;
  onCountryChange?: any;
  error?: boolean;
  errorMessage?: string;
  onClearField?: () => void;
  phoneValue?: string;
  controlledCountryCode?: string;
  defaultNumber?: string;
  onBlur?: () => void;
  showCountryName?: boolean;
  showCountryCode?: boolean;
  showCountryCodeLeadPrefixes?: boolean;
  positionSearchable?: "absolute" | "fixed";
}

interface HeroBanner {
  image: Media;
  title: string;
}

export interface HeroBannerProps {
  entry: HeroBanner;
}

export interface FaqBoxItem {
  question: string;
  answer: {
    html: string;
  };
  cta?: CustomLink;
  title?: string;
  subtitle?: string;
  icon?: CustomLink;
  items: {
    question: string;
    answer: {
      html: string;
    };
  }[];
}

export interface QAContentProps {
  entry: {
    question: string;
    answer: {
      html: string;
    };
  }[];
}

export interface FaqAccordionItem {
  title: string;
  subTitle: string;
  icon: CustomLink;
  variant?: string;
  items: FaqBoxItem[];
  accordionItems: FaqBoxItem[];
  overtitle?: string;
}

export interface FAQProps {
  entry: FaqAccordionItem;
}

interface FaqBox {
  title: string;
  subTitle: string;
  overtitle?: string;
  icon?: CustomLink;
  isHighlight?: boolean;
  items: FaqBoxItem[];
  variant?: string;
  primaryFont?: "Termina" | "El Messiri" | "Josefin Sans";
  primaryColor?: string;
  backgroundColor?: string;
  accentColor?: string;
}

export interface FaqBoxProps {
  entry: FaqBox;
  isInventoryVairant?: boolean;
  fontFamily?: string;
}

export interface FaqTemplateProps {
  heroBanner: HeroBannerProps;
  faqList: FaqBoxProps[];
}

export type HubTemplateHeroPodcast = {
  tabs: CustomLink[];
};

export type HubTemplateHeroEntry = {
  id: string;
  image?: Media;
  logo?: Media;
  title?: string;
  abstract?: string;
  cta?: CustomLink;
  variant?: string;
  podcast?: HubTemplateHeroPodcast;
};

export interface HubTemplateHeroProps {
  entry: HubTemplateHeroEntry;
}

export enum ArticleSorting {
  featured = "Featured",
  mostRecent = "Most Recent",
}
export interface DHubIntroProps extends BaseEntry {
  logo: ImageElement;
  articleList?: Article[];
  tabs: DHubIntroTab[];
  cta: CustomLink;
  fallbackImage?: ImageElement;
}

export interface DHubIntroTab extends BaseEntry {
  title: string;
  articlesType: ArticleTypes;
  sorting: ArticleSorting;
  cta: CustomLink;
}

export interface RealtimeBarProjectLaunchProps {
  entry: CommercetoolsProject;
}

export interface TabNavigationProps {
  entry: TabNavigation;
}
export interface TabNavigation {
  tabs: CustomLink[];
}

export interface ContactServicesCardProps {
  title: string;
  text: string;
  ctas: CustomLink[];
}

export interface ContactServicesEntry {
  title: string;
  subtitle: string;
  cards: ContactServicesCardProps[];
}
export interface ContactServicesProps {
  entry: ContactServicesEntry;
}

/* OfficeAddressProps */

export interface OfficeProps {
  title: string;
  departmentArea?: string[];
  phoneNumber?: number | string;
  email?: string;
  location?: string;
  position?: Position;
}

export interface OfficeListProps {
  name: string;
  items: OfficeProps[];
}
export interface OfficeListEntry {
  items: OfficeListProps[];
}
export interface OfficeAddressProps {
  entry: OfficeListEntry;
}
export interface Editorial3ColumnsCardListProps {
  entry: Editorial3ColumnsCardList;
}
export interface Editorial3ColumnsCardList {
  title?: string;
  cards: Card[];
}
export interface Card {
  title: string;
  subtitle?: string;
  image: ImageElement;
  modal?: SingleCardMemberModal;
}
export interface SingleCardMemberModal extends BaseEntry {
  title: string;
  image: ImageElement;
  name: string;
  role: string;
  body: {
    html: string;
  };
  cta?: CustomLink;
}
export interface textImage extends BaseEntry {
  title: string;
  text: string;
  image: ImageElement;
  cta?: CustomLink;
}
export interface text extends BaseEntry {
  title: string;
  text: string;
}

export interface PageIntroProps {
  entry: PageIntro;
}
export interface PageIntro extends BaseEntry {
  description: string;
  cta: CustomLink;
  jobRole: textImage[];
  dataIdentity: text[];
}

export interface LabelValue {
  label?: string | React.ReactNode;
  value?: string | number;
  type?: string;
  active?: boolean;
  [key: string]: any;
}

export interface GroupedLabelValue {
  [key: string]: LocationDB[];
}

export interface SelectOptions extends LabelValue {
  disabled?: boolean;
}

export interface IHeroBannerOOS extends BaseEntry {
  entry: {
    backgroundImage: {
      title: string;
      description: string;
      url: string;
      [key: string]: any;
    };
    bannerTitle: string;
    breadcrumb: BreadcrumbEntry;
  };
}

export interface IBackNavigation {
  crumbs: BreadcrumbEntry;
}

export interface ISingleColumnRichText extends BaseEntry {
  entry: {
    title: string;
    description: {
      html: string;
    };
    cta: IKeyValue[];
  };
}
export interface IKeyValue extends BaseEntry {
  key: string;
  value: string;
}

export interface IDocument {
  id: string;
  type: string;
  name: string;
  year: IDocumentYear;
  doc: {
    url: string;
  };
  icon: {
    icon: string;
  };
  cta: CustomLink[];
}

export interface IDocumentListing {
  id: string;
  type: string;
  internalName: string;
  description?: {
    html: string;
  };
  documents: IDocument[];
}

export interface IDocumentListingProps {
  entry: IDocumentListing;
}
export interface Pagination {
  total: number;
  current: number;
  size: number;
}

export interface Location {
  id: string;
  name: string;
  type: "Country" | "City" | "Area" | "Location";
  products: number;
}

export interface SearchParams {
  locale?: string;
  category?: string | string[];
  type?: string;
  propertyType?: string[];
  bedrooms?: string | string[];
  bathrooms?: number;
  minimumUnitPrice?: number;
  maximumUnitPrice?: number;
  minimumUnitArea?: number;
  maximumUnitArea?: number;
  status?: string;
  isFurnished?: string;
  unitViews?: string;
  features?: string;
  deliveryDate?: string;
  launchDate?: string;
  lifeStyle?: string;
  brands?: string;
  others?: string;
  offers?: string;
  limit?: number;
  offset?: number;
  sort?: string;
  withResults?: boolean;
  withFilters?: boolean;
  withLocations?: boolean;
  customIDList?: string;
  currency?: string;
  measurement?: string;
  year?: string[];
  price?: number[];
  area?: number[];
  onlyAvailable?: boolean;
}
export interface Payload {
  param: string;
  value: any;
}
export type ParamsAction = {
  action: "UPDATE" | "SEARCH" | "COUNT" | "CLEAR" | "REMOVE";
  // payload: Payload | Payload[];
  payload: any;
};

export interface SegmentedControlTab extends BaseEntry {
  label: string;
  components: Entry[];
}

export interface ISingleColumnRichText extends BaseEntry {
  entry: {
    title: string;
    description: {
      html: string;
    };
    cta: IKeyValue[];
  };
}
export interface IKeyValue extends BaseEntry {
  key: string;
  value: string;
}

// Define the types for the header and data items
export interface IPaymentPlanHeader {
  milestone: string;
  percentage: string;
  currency: string;
}

export interface PaymentPlanColumnData {
  milestone: string;
  percentage: number;
  currency: number;
}

export interface PaymentPlanTableProps {
  product: CommercetoolsProject;
}

// GENERIC PERSONAL AREA
export interface PersonalAreaProps {
  entry: PersonalAreaEntry;
}
export interface ProfileImagesProps {
  damacLivingImage: ImageElement;
  welcomeImage: ImageElement;
}
export interface PersonalAreaEntry {
  profile: AccountProfile;
  appointments: AppointmentsProps;
  appointmentsHistory: AppointmentsProps;
  saved: AccountSaved;
  properties: PropertyProps | any;
  reservation: ReservationProps;
  reservationsHistory: ReservationProps;
  profileImages: ProfileImagesProps;
}
export interface AccountProfile {
  id: string;
  first_name?: string;
  last_name?: string;
  fullName?: string;
  email: string;
  gender: string;
  mobile: string;
  passport_number: string;
  nationality: string;
  birthdate: string;
  address: string;
  city: string;
  zipcode: string;
  country: string;
  source: string;
}

// APPOINTMENTS
export interface AppointmentDataProps2 {
  appointments: SingleAppointmentDataProps[];
  appointmentsHistory: AppointmentDataProps[];
}
export interface AppointmentDataProps {
  appointments: AppointmentDataProps2;
  appointmentsHistory: AppointmentDataProps2;
}

export interface SingleAppointmentDataProps {
  agent: string;
  type: string;
  meeting_type?: string;
  attendance?: string;
  about_to_happen: boolean;
  appointment_id: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  appointment_agent: string;
  client_name: string;
  client_email: string;
  client_phone: string;
  appointmentLocation: string;
  appointment_type: string;
  appointment_description: string;
  appointment_duration: string;
  appointment_status: string;
  appointment_address: string;
  appointment_url: string;
  appointment_call: string;
  appointment_place: string;
  unit?: AppointmentUnit | AppointmentUnit[];
}

export interface AppointmentsProps {
  appointments: AppointmentDataProps[];
  appointmentsHistory: AppointmentDataProps[];
}

// ACCOUNT
export interface AccountSaved {}
export interface AccountProperties {}

// MY PROPERTIES
export interface PropertyProps {
  entry: PropertyEntry[];
}

export interface PropertyEntry {
  areaSft: 460;
  bedrooms: number;
  bedroomType: "STD";
  buildingCode: "ARTD";
  cta: CustomLink;
  countryOfResidence: string;
  customerName: string;
  customerPhone: string;
  drupalID: number;
  emailAddress: string;
  images: any[];
  location: string;
  nationality: string;
  passportNumber: string;
  regId: string;
  requestedPrice: number;
  status: string;
  tags: any[];
  totalPaid: string | number;
  unitId: string;
  unitName: string;
  unitPlan: string;
  unitType: string;
}

export enum VISUAL_VARIANT {
  DARK = "dark",
  LIGHT = "light",
  WHITE = "white",
}

// Mortgage Calculator Types - Start
export interface IMortgageCalculatorLabel {
  label?: string;
  inputLabel?: string;
}

export interface IMortgageCalculatorLabelIcon {
  icon?: string;
  iconTitle?: string;
}

export interface IMortgageCalculatorInput {
  customInputClass?: string;
  inputName: string;
  inputValue?: number | string;
  inputPlaceholder?: string;
  handleChange?: (value: string) => void;
  setDefaultValue?: () => void;
}

// Define the combined interface for the component props
export interface IMortgageCalculatorInputAndLabel extends IMortgageCalculatorLabel, IMortgageCalculatorLabelIcon, IMortgageCalculatorInput {}

export interface IMortgageCalculatorLabelAndIcon extends IMortgageCalculatorLabel, IMortgageCalculatorLabelIcon {}
export interface ContactFormProps {
  entry: ContactForm;
}

export interface ContactForm extends BaseEntry {
  backgroundImage: Media;
}

export interface CreateLeadModalProps {
  mode: string;
  closeModal: () => void;
  // settings: { [key: string]: any };
}

export interface CreateGenericRequestModalProps {
  closeModal: () => void;
}

export interface ContactUsModalProps {
  open: boolean;
  location: string;
  closeModal: () => void;
}
export interface CommercetoolsOrder {
  id: string;
  version: number;
  orderNumber: string;
  purchaseOrderNumber?: string;
  customerId?: string;
  customerEmail?: string;
  customerGroup?: CustomerGroupReference;
  anonymousId?: string;
  businessUnit?: BusinessUnitKeyReference;
  store?: StoreKeyReference;
  lineItems: LineItem[];
  customLineItems: CustomLineItem[];
  totalPrice: TypedMoney;
  taxedPrice?: TaxedPrice;
  taxedShippingPrice?: TaxedPrice;
  discountOnTotalPrice?: DiscountOnTotalPrice;
  taxMode: TaxMode;
  taxRoundingMode: RoundingMode;
  taxCalculationMode: TaxCalculationMode;
  inventoryMode: InventoryMode;
  billingAddress?: Address;
  shippingAddress?: Address;
  shippingMode: ShippingMode;
  shippingKey?: string;
  shippingInfo?: ShippingInfo;
  shippingRateInput?: ShippingRateInput;
  shippingCustomFields?: CustomFields;
  shipping?: Shipping[];
  itemShippingAddresses?: Address[];
  discountCodes?: DiscountCodeInfo[];
  directDiscounts?: DirectDiscount[];
  refusedGifts?: CartDiscountReference[];
  paymentInfo?: PaymentInfo;
  country?: CountryCode;
  locale?: Locale;
  origin: CartOrigin;
  cart?: CartReference;
  quote?: QuoteReference;
  orderState: OrderState;
  shipmentState?: ShipmentState;
  paymentState?: PaymentState;
  state?: StateReference;
  syncInfo?: SyncInfo[];
  returnInfo?: ReturnInfo[];
  completedAt?: Date;
  custom?: CustomFields;
  createdAt: Date;
  createdBy?: CreatedBy;
  lastModifiedAt: Date;
  lastModifiedBy?: LastModifiedBy;
}

export interface CompactModal {
  title: string;
  image: ImageElement;
  bodyTitle?: string;
  bodySubtitle?: string;
  bodyText: string;
  onClose?: Function;
  cta?: CustomLink;
}
export interface Map extends BaseEntry {
  tabs: MapTab[];
  center: { lat: number; lon: number };
  zoom: number;
  interactive: boolean;
}

export interface MapTab extends BaseEntry {
  title: string;
  countries: string[];
  modal: MapTabModal;
}

export interface MapTabModal extends BaseEntry {
  title: string;
  logo?: ImageElement;
  text: string;
  cta?: CustomLink;
}

type CustomerGroupReference = any; // Replace with actual CustomerGroupReference type
type BusinessUnitKeyReference = any; // Replace with actual BusinessUnitKeyReference type
type StoreKeyReference = any; // Replace with actual StoreKeyReference type
type LineItem = any; // Replace with actual LineItem type
type CustomLineItem = any; // Replace with actual CustomLineItem type
type TypedMoney = any; // Replace with actual TypedMoney type
type TaxedPrice = any; // Replace with actual TaxedPrice type
type DiscountOnTotalPrice = any; // Replace with actual DiscountOnTotalPrice type
type TaxMode = any; // Replace with actual TaxMode type
type RoundingMode = any; // Replace with actual RoundingMode type
type TaxCalculationMode = any; // Replace with actual TaxCalculationMode type
type InventoryMode = any; // Replace with actual InventoryMode type
type Address = any; // Replace with actual Address type
type ShippingMode = any; // Replace with actual ShippingMode type
type ShippingInfo = any; // Replace with actual ShippingInfo type
type ShippingRateInput = any; // Replace with actual ShippingRateInput type
type CustomFields = any; // Replace with actual CustomFields type
type Shipping = any; // Replace with actual Shipping type
type DiscountCodeInfo = any; // Replace with actual DiscountCodeInfo type
type DirectDiscount = any; // Replace with actual DirectDiscount type
type CartDiscountReference = any; // Replace with actual CartDiscountReference type
type PaymentInfo = any; // Replace with actual PaymentInfo type
type CountryCode = string; // Replace with actual CountryCode type if different
type Locale = string; // Replace with actual Locale type if different
type CartOrigin = any; // Replace with actual CartOrigin type
type CartReference = any; // Replace with actual CartReference type
type QuoteReference = any; // Replace with actual QuoteReference type
type OrderState = any; // Replace with actual OrderState type
type ShipmentState = any; // Replace with actual ShipmentState type
type PaymentState = any; // Replace with actual PaymentState type
type StateReference = any; // Replace with actual StateReference type
type SyncInfo = any; // Replace with actual SyncInfo type
type ReturnInfo = any; // Replace with actual ReturnInfo type
type CreatedBy = any; // Replace with actual CreatedBy type
type LastModifiedBy = any; // Replace with actual LastModifiedBy type

export interface IDocumentYear {
  id: string;
  type?: string;
  entryTitle: string;
  year: number;
}
export interface IDocumentCategory {
  name: string;
  categoryName: string;
  pageName: string;
  years: IDocumentYear[];
  documentListing: IDocumentListing[];
  type?: string;
}

export interface IDocumentCategoriesListProps {
  entry: {
    name: string;
    categories: IDocumentCategory[];
  };
  searchParams: {
    category: string;
    year: string;
  };
}
export interface IYearSelectProps {
  years: IDocumentYear[];
  selectedYear: number | null;
  clickHandler: (year: number) => void;
}

export interface LocationDB {
  id: string;
  name: string;
  type: string;
  products: number;
  country?: string;
  location?: string;
  area?: string;
  city?: string;
  active?: boolean;
  order?: number;
}

export interface MapPinDB {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  categoryID: string;
  categoryName: string;
  categories: { id: string; type: string; name: string }[];
  active?: boolean;
  order?: number;
}

export interface SettingsDB {
  id: number;
  currency: string;
  measurement: string;
  lastProjectUpdate: Date;
  lastPropertyTypeUpdate: Date;
  lastLocationsUpdate: Date;
  lastPinUpdate: Date;
}

export interface WorkerMessageT<T> {
  type: "init" | "data" | "error" | "stop";
  payload?: {
    id?: string;
    data: T;
  };
}

export enum WORKER_ACTION {
  COMPLETED = "complete",
  INIT = "init",
  UPDATE = "update",
  SETUP = "setup",
}

export enum PRODUCT_TYPE {
  PROPERTY_TYPE = "propertyType",
  UNIT_TYPE = "unit-type",
  PROJECT = "project",
}

export interface Community {
  categoryId: string;
  slug: string;
}
